/**
 * SunCore - High-fidelity solar surface
 * Simplified approach for R3F compatibility
 */

import { useRef, useMemo } from 'react';
import { useFrame, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { shaderMaterial } from '@react-three/drei';

// Create a custom shader material using drei's shaderMaterial
const SunMaterial = shaderMaterial(
  {
    uTime: 0,
    uColorCore: new THREE.Color(0xffffff),
    uColorMid: new THREE.Color(0xffdd44),
    uColorOuter: new THREE.Color(0xff6600),
    uTurbulence: 1.0,
  },
  // Vertex shader
  `
    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec2 vUv;

    void main() {
      vNormal = normalize(normalMatrix * normal);
      vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment shader
  `
    uniform float uTime;
    uniform vec3 uColorCore;
    uniform vec3 uColorMid;
    uniform vec3 uColorOuter;
    uniform float uTurbulence;

    varying vec3 vNormal;
    varying vec3 vPosition;
    varying vec2 vUv;

    // Simplex noise
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

    float snoise(vec3 v) {
      const vec2 C = vec2(1.0/6.0, 1.0/3.0);
      const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

      vec3 i = floor(v + dot(v, C.yyy));
      vec3 x0 = v - i + dot(i, C.xxx);

      vec3 g = step(x0.yzx, x0.xyz);
      vec3 l = 1.0 - g;
      vec3 i1 = min(g.xyz, l.zxy);
      vec3 i2 = max(g.xyz, l.zxy);

      vec3 x1 = x0 - i1 + C.xxx;
      vec3 x2 = x0 - i2 + C.yyy;
      vec3 x3 = x0 - D.yyy;

      i = mod289(i);
      vec4 p = permute(permute(permute(
        i.z + vec4(0.0, i1.z, i2.z, 1.0))
        + i.y + vec4(0.0, i1.y, i2.y, 1.0))
        + i.x + vec4(0.0, i1.x, i2.x, 1.0));

      float n_ = 0.142857142857;
      vec3 ns = n_ * D.wyz - D.xzx;

      vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

      vec4 x_ = floor(j * ns.z);
      vec4 y_ = floor(j - 7.0 * x_);

      vec4 x = x_ * ns.x + ns.yyyy;
      vec4 y = y_ * ns.x + ns.yyyy;
      vec4 h = 1.0 - abs(x) - abs(y);

      vec4 b0 = vec4(x.xy, y.xy);
      vec4 b1 = vec4(x.zw, y.zw);

      vec4 s0 = floor(b0) * 2.0 + 1.0;
      vec4 s1 = floor(b1) * 2.0 + 1.0;
      vec4 sh = -step(h, vec4(0.0));

      vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
      vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

      vec3 p0 = vec3(a0.xy, h.x);
      vec3 p1 = vec3(a0.zw, h.y);
      vec3 p2 = vec3(a1.xy, h.z);
      vec3 p3 = vec3(a1.zw, h.w);

      vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
      p0 *= norm.x;
      p1 *= norm.y;
      p2 *= norm.z;
      p3 *= norm.w;

      vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
      m = m * m;
      return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
    }

    float fbm(vec3 p) {
      float value = 0.0;
      float amplitude = 0.5;
      for (int i = 0; i < 4; i++) {
        value += amplitude * snoise(p);
        p *= 2.0;
        amplitude *= 0.5;
      }
      return value;
    }

    void main() {
      vec3 normalizedPos = normalize(vPosition);
      float time = uTime * 0.1;
      vec3 noisePos = normalizedPos * 3.0;

      // Multi-scale turbulence
      float largeScale = fbm(noisePos + time * 0.3) * 0.5 + 0.5;
      float medScale = fbm(noisePos * 5.0 + time * 0.5) * 0.3;
      float fineScale = snoise(noisePos * 15.0 + time * 1.5) * 0.15;

      float turbulence = largeScale + medScale + fineScale;
      turbulence = clamp(turbulence, 0.0, 1.0);

      // Color gradient
      vec3 color;
      if (turbulence < 0.4) {
        color = mix(uColorOuter, uColorMid, turbulence / 0.4);
      } else if (turbulence < 0.7) {
        color = mix(uColorMid, uColorCore, (turbulence - 0.4) / 0.3);
      } else {
        color = mix(uColorCore, vec3(1.0, 1.0, 0.95), (turbulence - 0.7) / 0.3);
      }

      // Limb darkening
      vec3 viewDir = normalize(cameraPosition - vPosition);
      float limbFactor = pow(max(dot(vNormal, viewDir), 0.0), 0.5);
      color = mix(uColorOuter * 0.5, color, limbFactor);

      // Intensity boost
      color *= 1.0 + uTurbulence * turbulence * 0.5;

      gl_FragColor = vec4(color, 1.0);
    }
  `
);

// Extend R3F to recognize our custom material
extend({ SunMaterial });

// Add type declaration
declare global {
  namespace JSX {
    interface IntrinsicElements {
      sunMaterial: any;
    }
  }
}

interface SunCoreProps {
  position?: [number, number, number];
  radius?: number;
  intensity?: number;
}

export function SunCore({
  position = [-30, 0, 0],
  radius = 5,
  intensity = 1.0
}: SunCoreProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.ShaderMaterial>(null);

  useFrame((state) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = state.clock.elapsedTime;
    }
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.0005;
    }
  });

  return (
    <group position={position}>
      {/* Main sun sphere with custom shader */}
      <mesh ref={meshRef}>
        <sphereGeometry args={[radius, 128, 128]} />
        <sunMaterial
          ref={materialRef}
          uTurbulence={intensity}
        />
      </mesh>

      {/* Inner glow sphere - Enhanced for bloom */}
      <mesh scale={1.05}>
        <sphereGeometry args={[radius, 64, 64]} />
        <meshBasicMaterial
          color={0xffcc00}
          transparent
          opacity={0.4}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Corona glow - brighter for cinematic bloom */}
      <mesh scale={1.3}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0xff7700}
          transparent
          opacity={0.25}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      <mesh scale={1.6}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0xff5500}
          transparent
          opacity={0.15}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      <mesh scale={2.0}>
        <sphereGeometry args={[radius, 32, 32]} />
        <meshBasicMaterial
          color={0xff3300}
          transparent
          opacity={0.08}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>

      {/* Point light (primary scene lighting) */}
      <pointLight
        color={0xffffee}
        intensity={intensity * 20}
        distance={250}
        decay={2}
      />
    </group>
  );
}
